import React, { useState } from "react";
import { bool } from "prop-types";
import { Col, Row } from "react-bootstrap";
import * as i18n from "util/i18n";
import { useLocation } from "react-router-dom";
import { actions as debinActions } from "reducers/debin";
import { useTransaction } from "hooks/useTransactions";
import Button from "pages/_components/Button";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import useReactPrint from "pages/_components/Print/useReactToPrint";
import { TransactionCard, TransactionHeader, TransactionSign } from "pages/_components/TransactionCard";
import Notification from "pages/_components/Notification";
import CustomData from "pages/_components/CustomData/CustomData";
import CustomText from "pages/_components/CustomText/CustomText";
import FormattedDate from "pages/_components/FormattedDate";
import FormattedAmount from "pages/_components/FormattedAmount";
import Avatar from "pages/_components/Avatar";
import GridTable from "pages/_components/GridTable/GridTable";

const DeclineDebinTransactionPage = ({ isDesktop }) => {
    const { state = {} } = useLocation();
    const { isFlowComplete = false } = state;

    const formatTransaction = (data, idTransaction) => ({ ...data, idTransaction });

    const {
        isLoading,
        transaction,
        dispatch,
        handleSignTransaction,
        showSignButton,
        push,
        id,
        status,
    } = useTransaction(
        "/debinPayments/action/reject",
        debinActions.setDetail,
        debinActions.cleanDebinTicket,
        formatTransaction,
    );
    const { componentRef, handlePrint, mobileRef, handleShareMobile } = useReactPrint("Rechazar DEBIN", id);
    const backLinkTo = isFlowComplete ? "/debinPayments" : "";
    const [showMoreData, setShowMoreData] = useState(!isDesktop);
    const [buttonLabel, setButtonLabel] = useState("echecks.seeMore.button");
    const handleSeeMoreData = () => {
        setShowMoreData(!showMoreData);
        if (!showMoreData) {
            setButtonLabel("echecks.seeLess.button");
        } else {
            setButtonLabel("echecks.seeMore.button");
        }
    };

    const handleDownload = async () => {
        await setShowMoreData(true);
        return handlePrint();
    };

    const debinData = transaction?.data;

    return (
        <>
            <Notification scopeToShow="debin.reject.notification" />
            <ResponsiveHeader ticket metaTitle="debin.reject.debin" backLinkTo={backLinkTo} />
            <MainContainer showLoader={isLoading}>
                <div className="container" ref={componentRef}>
                    <div ref={mobileRef}>
                        <TransactionCard transaction={transaction}>
                            <TransactionHeader
                                title="debin.reject.debin"
                                subTitle={`forms.transaction.ticket.status.${transaction.idTransactionStatus}`}
                            />
                            <div className="ticket">
                                <div className="ticket__wrapper">
                                    <div className="ticket__header" />
                                    <div className="ticket__body ticket__full-width">
                                        <GridTable>
                                            <GridTable.Container className="justify-content-around">
                                                <GridTable.Data
                                                    className="d-flex align-items-center"
                                                    rowStart={isDesktop ? 1 : 2}
                                                    columnStart={1}
                                                    columnWidth={1}>
                                                    {isDesktop && (
                                                        <Avatar
                                                            user={{
                                                                userFullName: debinData?.sellerName,
                                                                defaultAvatarId: 0,
                                                            }}
                                                        />
                                                    )}

                                                    <CustomData
                                                        dataClass={!isDesktop ? "align-items-center" : ""}
                                                        titleText="debin.seller"
                                                        titleTextType="I18N"
                                                        valueText={
                                                            <>
                                                                <CustomText
                                                                    className="data-label"
                                                                    text={`${debinData?.sellerName} `}
                                                                />
                                                                <br />
                                                                {isDesktop && (
                                                                    <CustomText
                                                                        className="regular"
                                                                        text={`${debinData?.sellerCuit} `}
                                                                    />
                                                                )}
                                                            </>
                                                        }
                                                    />
                                                </GridTable.Data>
                                                {!isDesktop && (
                                                    <GridTable.Data rowStart={3} columnWidth={1}>
                                                        <CustomData
                                                            dataClass={!isDesktop ? "align-items-center" : ""}
                                                            titleText="CUIT"
                                                            titleTextType="TEXT"
                                                            valueText={debinData?.sellerCuit.split(" ")[1]}
                                                        />
                                                    </GridTable.Data>
                                                )}
                                                <GridTable.Data
                                                    rowStart={1}
                                                    columnWidth={1}
                                                    className={!isDesktop ? "d-flex justify-content-center" : ""}>
                                                    <CustomData
                                                        titleText="debin.amount"
                                                        titleTextType="I18N"
                                                        dataClass={`amount tag tag-${
                                                            status.isPending ? "warning" : "echeq-character"
                                                        } ${!isDesktop ? "d-flex align-items-center" : ""}`}
                                                        valueText={
                                                            <FormattedAmount
                                                                currency={debinData?.curencySeller}
                                                                quantity={debinData?.amount}
                                                            />
                                                        }
                                                    />
                                                </GridTable.Data>
                                            </GridTable.Container>
                                        </GridTable>
                                    </div>
                                    <div className="ticket__body">
                                        <div className="view-more-data-button" id="element-to-ignore">
                                            <div className="ticket__divider inside-ticket__body">
                                                <div className="ticket__notch ticket__notch--left"> </div>
                                                <div className="ticket__notch ticket__notch--right"> </div>
                                            </div>
                                            <Button
                                                aria-expanded={showMoreData ? "true" : "false"}
                                                className="more-button"
                                                label={buttonLabel}
                                                bsStyle="link"
                                                onClick={handleSeeMoreData}
                                                image={
                                                    showMoreData
                                                        ? "figma/icon/arrow-up.svg"
                                                        : "figma/icon/arrow-down.svg"
                                                }
                                            />
                                        </div>
                                    </div>

                                    <div className={`${showMoreData ? "expand " : "collapse"}`}>
                                        <div className="ticket__body ticket__full-width">
                                            <GridTable>
                                                <GridTable.Container>
                                                    <GridTable.Data columnStart={1} rowStart={1}>
                                                        <CustomData
                                                            titleText="debin.solicit.date"
                                                            titleTextType="I18N"
                                                            valueText={
                                                                <FormattedDate
                                                                    date={debinData?.solicitDate}
                                                                    dateFormat="DD/MM/YYYY"
                                                                />
                                                            }
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data
                                                        columnStart={isDesktop ? 2 : 1}
                                                        rowStart={isDesktop ? 1 : 2}>
                                                        <CustomData
                                                            titleText="debin.expirery.date"
                                                            titleTextType="I18N"
                                                            valueText={
                                                                <FormattedDate
                                                                    date={debinData?.dueDate}
                                                                    dateFormat="DD/MM/YYYY HH:mm:ss"
                                                                />
                                                            }
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={1} rowStart={isDesktop ? 2 : 3}>
                                                        <CustomData
                                                            titleText="debin.id"
                                                            titleTextType="I18N"
                                                            valueText={debinData?.debinId}
                                                        />
                                                    </GridTable.Data>

                                                    <GridTable.Data
                                                        columnStart={isDesktop ? 2 : 1}
                                                        rowStart={isDesktop ? 2 : 4}>
                                                        <CustomData
                                                            titleText="debin.cbu"
                                                            titleTextType="I18N"
                                                            valueText={debinData?.sellerCbu}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={1} rowStart={isDesktop ? 3 : 2}>
                                                        <CustomData
                                                            titleText="debin.alias"
                                                            titleTextType="I18N"
                                                            valueText={debinData?.alias}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={isDesktop ? 2 : 1} rowStart={3}>
                                                        <CustomData
                                                            titleText="debin.bank.branch"
                                                            titleTextType="I18N"
                                                            valueText={debinData?.bank}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={1} rowStart={4}>
                                                        <CustomData
                                                            titleText="debin.concept.label"
                                                            titleTextType="I18N"
                                                            valueText={debinData?.concept}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data
                                                        columnStart={isDesktop ? 2 : 1}
                                                        rowStart={isDesktop ? 4 : 5}>
                                                        <CustomData
                                                            titleText="debin.description.label"
                                                            titleTextType="I18N"
                                                            valueText={debinData?.description}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={1} rowStart={isDesktop ? 5 : 6}>
                                                        <CustomData
                                                            titleText="debin.reference"
                                                            titleTextType="I18N"
                                                            valueText={debinData?.reference}
                                                        />
                                                    </GridTable.Data>
                                                </GridTable.Container>
                                            </GridTable>

                                            <TransactionSign isDesktop={isDesktop} showMoreData={showMoreData} />
                                        </div>
                                    </div>
                                    <div className="ticket__footer" data-copytext={i18n.get("global.ticketCopyText")} />
                                </div>
                            </div>
                        </TransactionCard>
                    </div>
                    <Row className="form-actions no-print">
                        <Col xs={12} md={4} mdOffset={!showSignButton && !status.isFinished ? 4 : 2}>
                            <Button
                                onClick={() => dispatch(push("/desktop"))}
                                label="forms.transaction.ticket.actions.backToHome"
                                className={`btn${
                                    showSignButton || status.isFinished ? " btn-outline" : " btn-primary"
                                }`}
                            />
                        </Col>
                        {status.isFinished && (
                            <>
                                <Col xs={12} md={4}>
                                    <Button
                                        onClick={handleDownload}
                                        label="global.download"
                                        className="btn btn-primary"
                                        image="images/download.svg"
                                    />
                                </Col>
                                {!isDesktop && (
                                    <Col xs={12} md={4}>
                                        <Button
                                            className="btn btn-outline"
                                            image="images/figma/icon/share.svg"
                                            label="global.share"
                                            onClick={() => {
                                                handleShareMobile();
                                            }}
                                        />
                                    </Col>
                                )}
                            </>
                        )}
                        {showSignButton && (
                            <Col xs={12} md={4}>
                                <Button
                                    onClick={handleSignTransaction}
                                    label="forms.signTransaction.link"
                                    className="btn btn-primary"
                                />
                            </Col>
                        )}
                    </Row>
                </div>
            </MainContainer>
        </>
    );
};

DeclineDebinTransactionPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default DeclineDebinTransactionPage;
